import { forwardRef, useImperativeHandle, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import type { PriceChange } from '@/lib/cart-utils';

type PriceChangeDialogProps = {
  onConfirm: (priceChanges: PriceChange[]) => void;
};

export type PriceChangeDialogRef = {
  show: (priceChanges: PriceChange[]) => void;
};

const PriceChangeDialog = forwardRef<PriceChangeDialogRef, PriceChangeDialogProps>(({ onConfirm }, ref) => {
  const [open, setOpen] = useState(false);
  const [priceChanges, setPriceChanges] = useState<PriceChange[]>([]);

  useImperativeHandle(ref, () => ({
    show: (changes: PriceChange[]) => {
      setPriceChanges(changes);
      setOpen(true);
    },
  }));

  const handleConfirm = () => {
    onConfirm(priceChanges);
    setOpen(false);
    setPriceChanges([]);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      onConfirm(priceChanges);
      setPriceChanges([]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>메뉴 가격이 변경되었습니다</DialogTitle>
          <DialogDescription>
            장바구니의 가격이 자동으로 업데이트됩니다. 변경된 가격을 확인하신 후 다시 예약해주세요.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 my-4 max-h-60 overflow-y-auto">
          {priceChanges.map(change => (
            <div key={change.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="font-medium text-gray-900">
                {change.name}
                {change.option && <span className="text-sm text-gray-500 ml-1">({change.option})</span>}
              </div>
              <div className="text-sm mt-1">
                <span className="text-gray-500 line-through">{change.oldPrice.toLocaleString()}원</span>
                <span className="mx-2">→</span>
                <span className="text-blue-600 font-medium">{change.newPrice.toLocaleString()}원</span>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button onClick={handleConfirm} className="w-full">
            다시 확인
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

PriceChangeDialog.displayName = 'PriceChangeDialog';

export default PriceChangeDialog;
